import { IndirectTaxJurisdictionType } from '@commenda/api/indirect-tax/registrations/types';
import type { ILineItemInput } from '@commenda/api/new-sales-tax/calculate/calculate';
import { getStateName } from '../registrations/utils/getStateName';
import { TaxClassReason } from '../transactions/types';
import type {
  JurisdictionLine,
  LineItemSummary,
  RawCalculateResponse,
  TaxSummaryView,
} from './types';

export function computeTaxSummary(
  response: RawCalculateResponse | null | undefined,
  requestLineItems?: ILineItemInput[] | null
): TaxSummaryView | null {
  const calculation = response?.data;
  const rawLineItems = calculation?.line_items;
  if (!rawLineItems || rawLineItems.length === 0) return null;

  const lineItems: LineItemSummary[] = rawLineItems.map((item, idx) => ({
    lineItemId: item.line_item_id,
    index: item.line_number ?? idx + 1,
    taxCode: item.tax_code,
    taxableAmount: item.taxable_amount,
    effectiveRate: item.effective_tax_rate / 100,
    total_tax_due: item.total_tax_due,
    jurisdictions: (item.tax_breakdown ?? []).map((j) => ({
      name: j.jurisdiction_name,
      type: j.jurisdiction_type,
      effective_tax_rate: j.rate / 100,
      tax: (j.rate / 100) * item.taxable_amount,
      tax_class: j.tax_class,
      tax_class_reason: j.tax_class_reason,
    })),
  }));

  const taxableAmount = rawLineItems.reduce(
    (sum, item) => sum + item.taxable_amount,
    0
  );

  // Total amount of the invoice, matching the line-items editor total:
  // sum(quantity * unitPrice - discount). Falls back to the response amounts
  // when the request line items are unavailable.
  const totalAmount =
    requestLineItems && requestLineItems.length > 0
      ? requestLineItems.reduce(
          (sum, item) =>
            sum +
            (Number(item.quantity) || 0) * (Number(item.pricePerUnit) || 0) -
            (Number(item.discount) || 0),
          0
        )
      : rawLineItems.reduce(
          (sum, item) => sum + item.amount * item.quantity,
          0
        );

  // Aggregate jurisdictions across line items by name+type, summing the tax.
  // The rate displayed is the effective rate for the jurisdiction over the whole
  // invoice (jurisdiction tax / total amount), so it stays correct when the
  // invoice mixes taxable and non-taxable line items.
  const aggregated = new Map<string, JurisdictionLine>();
  lineItems
    .flatMap((line) => line.jurisdictions)
    .forEach((j) => {
      const key = `${j.name}__${j.type}`;
      const existing = aggregated.get(key);
      if (existing) {
        existing.tax += j.tax;
      } else {
        aggregated.set(key, { ...j });
      }
    });

  const jurisdictionSummary = Array.from(aggregated.values()).map(
    (eachJurisdiction) => ({
      ...eachJurisdiction,
      effective_tax_rate:
        totalAmount > 0 ? eachJurisdiction.tax / totalAmount : 0,
    })
  );

  // The engine rounds each line's tax before totalling, so summing the unrounded jurisdiction amounts
  // can land a cent away from the total it reports and records.
  const salesTax =
    calculation?.total_tax ??
    jurisdictionSummary.reduce(
      (totalSalesTax, eachJurisdiction) => totalSalesTax + eachJurisdiction.tax,
      0
    );

  const effectiveRate = jurisdictionSummary.reduce(
    (totalEffectiveRate, eachJurisdiction) =>
      totalEffectiveRate + eachJurisdiction.effective_tax_rate,
    0
  );

  const allJurisdictions = lineItems.flatMap((line) => line.jurisdictions);
  const isMissingRegistration =
    allJurisdictions.length > 0 &&
    allJurisdictions.every(
      (j) => j.tax_class_reason === TaxClassReason.NO_REGISTRATION
    );
  const stateJurisdiction = allJurisdictions.find(
    (j) => j.type === IndirectTaxJurisdictionType.STATE_OR_PROVINCE
  );
  const shipTo = calculation?.addresses?.ship_to;
  // The state jurisdiction comes back as a code ("CA"), so resolve it to the
  // full state name before it is shown to the user.
  const unregisteredState = stateJurisdiction?.name || shipTo?.state;

  return {
    unregisteredJurisdictionName: isMissingRegistration
      ? (unregisteredState
          ? getStateName(unregisteredState, shipTo?.country)
          : shipTo?.country) || null
      : null,
    currency: calculation?.transaction_currency || 'USD',
    taxableAmount,
    effectiveRate,
    salesTax,
    jurisdictions: jurisdictionSummary,
    lineItems,
  };
}
